import React from "react";
import { MapPin } from "lucide-react";

/**
 * Talent Matching Visual, polished dark map-panel card.
 *
 * Design: reads as a modern location-intelligence panel. Dark navy base,
 * subtle blue grid + implied roads, a crisp project pin, three coverage
 * radii (Local First / Regional / Travel Ready) in the Onward palette,
 * and a small legend. No radar aesthetic, no beige, no em dashes.
 */
const TalentMatchingVisual = () => {
  return (
    <figure className="tmv" aria-label="Illustrative staffing coverage. This graphic explains how Onward matches talent by beginning near the project, expanding regionally, and then considering experienced travel ready professionals. The markers shown are illustrative and do not represent actual candidate locations or current availability.">
      <style>{`
        .tmv {
          margin: 0;
          width: 100%;
          max-width: 640px;
          background:
            linear-gradient(180deg, #0B1420 0%, #0E0F14 100%);
          border: 1px solid rgba(244,241,236,0.10);
          border-radius: 12px;
          box-shadow:
            0 10px 30px rgba(0,0,0,0.35),
            inset 0 1px 0 rgba(255,255,255,0.04);
          overflow: hidden;
        }
        .tmv__topbar {
          display: flex; align-items: center; justify-content: space-between;
          padding: 10px 14px;
          border-bottom: 1px solid rgba(244,241,236,0.08);
          background: linear-gradient(180deg, rgba(255,255,255,0.03), rgba(255,255,255,0));
        }
        .tmv__topbar__title {
          color: #F4F1EC; font-size: 11px; font-weight: 800; letter-spacing: 1.4px;
        }
        .tmv__topbar__pill {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 3px 8px;
          font-size: 10px; font-weight: 700; letter-spacing: 0.8px;
          color: #F4F1EC;
          background: rgba(148,180,220,0.10);
          border: 1px solid rgba(148,180,220,0.35);
          border-radius: 999px;
        }
        .tmv__topbar__pill__dot {
          display: inline-block; width: 6px; height: 6px; border-radius: 50%;
          background: #94B4DC;
          flex-shrink: 0;
        }
        .tmv__svg { display: block; width: 100%; height: auto; }
        .tmv__legend {
          padding: 10px 14px;
          border-top: 1px solid rgba(244,241,236,0.08);
          background: linear-gradient(180deg, rgba(255,255,255,0.02), rgba(255,255,255,0));
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 8px;
        }
        .tmv__leg { display: flex; align-items: center; gap: 8px; min-width: 0; }
        .tmv__leg__dot {
          width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0;
          box-shadow: 0 0 0 3px rgba(255,255,255,0.06);
        }
        .tmv__leg__txt { color: #F4F1EC; font-size: 11.5px; font-weight: 700; letter-spacing: 0.3px; }
        .tmv__leg__sub { color: #8fa0b8; font-size: 10.5px; font-weight: 500; }
        .tmv__caption {
          padding: 10px 14px 14px 14px;
          background: rgba(0,0,0,0.20);
          border-top: 1px solid rgba(244,241,236,0.06);
        }
        .tmv__caption p { margin: 0; color: #cbd5e1; font-size: 12.5px; line-height: 1.55; }
        .tmv__caption strong { color: #F4F1EC; }
        @media (max-width: 480px) {
          .tmv__legend { grid-template-columns: 1fr; gap: 4px; }
          .tmv__leg__sub { display: none; }
        }
      `}</style>

      {/* Top bar: gives the "product panel" feel */}
      <div className="tmv__topbar" aria-hidden="true">
        <span className="tmv__topbar__title">STAFFING COVERAGE</span>
        <span className="tmv__topbar__pill">
          <span className="tmv__topbar__pill__dot" />
          MATCHING APPROACH
        </span>
      </div>

      {/* Map panel */}
      <svg
        className="tmv__svg"
        viewBox="0 0 640 380"
        xmlns="http://www.w3.org/2000/svg"
        role="img"
        aria-hidden="true"
      >
        <defs>
          {/* Deep-blue map fill with a subtle vignette */}
          <radialGradient id="tmv-base" cx="50%" cy="50%" r="70%">
            <stop offset="0%" stopColor="#122844" />
            <stop offset="65%" stopColor="#0E1F36" />
            <stop offset="100%" stopColor="#0A1626" />
          </radialGradient>
          {/* Fine grid */}
          <pattern id="tmv-grid" x="0" y="0" width="32" height="32" patternUnits="userSpaceOnUse">
            <path d="M 32 0 L 0 0 0 32" fill="none" stroke="rgba(148,180,220,0.07)" strokeWidth="1" />
          </pattern>
          {/* Emphasis ticks every 128px */}
          <pattern id="tmv-grid-major" x="0" y="0" width="128" height="128" patternUnits="userSpaceOnUse">
            <path d="M 128 0 L 0 0 0 128" fill="none" stroke="rgba(148,180,220,0.14)" strokeWidth="1" />
          </pattern>
          {/* Local First ring glow */}
          <radialGradient id="tmv-local" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="rgba(200,16,46,0.28)" />
            <stop offset="100%" stopColor="rgba(200,16,46,0.02)" />
          </radialGradient>
          {/* Regional ring glow */}
          <radialGradient id="tmv-regional" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="rgba(217,116,35,0.16)" />
            <stop offset="100%" stopColor="rgba(217,116,35,0.02)" />
          </radialGradient>
          {/* Travel Ready ring glow */}
          <radialGradient id="tmv-travel" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="rgba(148,180,220,0.10)" />
            <stop offset="100%" stopColor="rgba(148,180,220,0.00)" />
          </radialGradient>
          {/* Pin drop shadow */}
          <filter id="tmv-pin-shadow" x="-50%" y="-50%" width="200%" height="200%">
            <feDropShadow dx="0" dy="2" stdDeviation="3" floodColor="#000" floodOpacity="0.55" />
          </filter>
        </defs>

        {/* Map base */}
        <rect x="0" y="0" width="640" height="380" fill="url(#tmv-base)" />
        <rect x="0" y="0" width="640" height="380" fill="url(#tmv-grid)" />
        <rect x="0" y="0" width="640" height="380" fill="url(#tmv-grid-major)" />

        {/* Implied road / county lines, a few crisp strokes suggest a real map without a fake blob */}
        <g stroke="rgba(148,180,220,0.20)" fill="none" strokeLinecap="round">
          {/* Primary corridors */}
          <path d="M -20 260 Q 180 240 320 250 T 660 230" strokeWidth="1.75" />
          <path d="M 40 -20 Q 120 140 240 210 T 420 380" strokeWidth="1.5" />
          <path d="M 660 100 Q 460 160 380 210 T 120 400" strokeWidth="1.25" />
          {/* Secondary streets: light dashed */}
          <path d="M 0 130 L 640 155" strokeWidth="0.75" strokeDasharray="3 6" opacity="0.6" />
          <path d="M 0 300 L 640 285" strokeWidth="0.75" strokeDasharray="3 6" opacity="0.6" />
          <path d="M 220 -10 L 240 380" strokeWidth="0.75" strokeDasharray="3 6" opacity="0.5" />
          <path d="M 480 -10 L 460 380" strokeWidth="0.75" strokeDasharray="3 6" opacity="0.5" />
        </g>

        {/* Coverage rings, filled glows so they read like real coverage, not radar sweeps */}
        {/* Travel Ready (outer) */}
        <circle cx="320" cy="205" r="175" fill="url(#tmv-travel)" stroke="rgba(148,180,220,0.30)" strokeDasharray="6 6" strokeWidth="1" />
        {/* Regional */}
        <circle cx="320" cy="205" r="112" fill="url(#tmv-regional)" stroke="rgba(217,116,35,0.55)" strokeDasharray="5 5" strokeWidth="1.5" />
        {/* Local First */}
        <circle cx="320" cy="205" r="58" fill="url(#tmv-local)" stroke="rgba(232,86,112,0.85)" strokeWidth="1.75" />

        {/* Ring labels, clean, small caps, high contrast */}
        <g fontFamily="ui-sans-serif, system-ui, sans-serif" fontWeight="800" letterSpacing="1.4">
          <text x="320" y="47" textAnchor="middle" fontSize="10" fill="#F4F1EC" opacity="0.9">TRAVEL READY</text>
          <text x="320" y="107" textAnchor="middle" fontSize="10" fill="#F4A261">REGIONAL</text>
          <text x="320" y="282" textAnchor="middle" fontSize="10" fill="#E85670">LOCAL FIRST</text>
        </g>

        {/* Radius tick + distance readout on right side of Local First */}
        <g>
          <line x1="320" y1="205" x2="378" y2="205" stroke="rgba(232,86,112,0.85)" strokeWidth="1" strokeDasharray="2 3" />
          <line x1="378" y1="199" x2="378" y2="211" stroke="rgba(232,86,112,0.85)" strokeWidth="1.25" />
          <text x="384" y="209" fontFamily="ui-sans-serif, system-ui, sans-serif" fontSize="10" fontWeight="700" fill="#F4F1EC">
            ~100 mi
          </text>
        </g>

        {/* Professional markers, clean UI dots (not random) */}
        {/* Local (inside inner ring) */}
        <g>
          <circle cx="290" cy="225" r="4" fill="#E85670" />
          <circle cx="290" cy="225" r="7" fill="none" stroke="rgba(232,86,112,0.4)" />
          <circle cx="345" cy="180" r="4" fill="#E85670" />
          <circle cx="345" cy="180" r="7" fill="none" stroke="rgba(232,86,112,0.4)" />
        </g>
        {/* Regional (between inner and middle) */}
        <g>
          <circle cx="245" cy="255" r="4" fill="#F4A261" />
          <circle cx="245" cy="255" r="7" fill="none" stroke="rgba(244,162,97,0.35)" />
          <circle cx="405" cy="150" r="4" fill="#F4A261" />
          <circle cx="405" cy="150" r="7" fill="none" stroke="rgba(244,162,97,0.35)" />
          <circle cx="395" cy="265" r="4" fill="#F4A261" />
          <circle cx="395" cy="265" r="7" fill="none" stroke="rgba(244,162,97,0.35)" />
        </g>
        {/* Travel ready (outer band) */}
        <g>
          <circle cx="170" cy="130" r="3.5" fill="#94B4DC" />
          <circle cx="170" cy="130" r="6.5" fill="none" stroke="rgba(148,180,220,0.35)" />
          <circle cx="490" cy="310" r="3.5" fill="#94B4DC" />
          <circle cx="490" cy="310" r="6.5" fill="none" stroke="rgba(148,180,220,0.35)" />
          <circle cx="160" cy="300" r="3.5" fill="#94B4DC" />
          <circle cx="160" cy="300" r="6.5" fill="none" stroke="rgba(148,180,220,0.35)" />
        </g>

        {/* Project pin, the anchor */}
        <g filter="url(#tmv-pin-shadow)">
          {/* pin body */}
          <path
            d="M 320 176
               C 306 176 296 187 296 200
               C 296 216 320 234 320 234
               C 320 234 344 216 344 200
               C 344 187 334 176 320 176 Z"
            fill="#D97423"
            stroke="#F4F1EC"
            strokeWidth="1.5"
          />
          <circle cx="320" cy="200" r="5" fill="#F4F1EC" />
        </g>
        {/* Project label */}
        <g>
          <rect x="266" y="150" width="108" height="20" rx="4" fill="#0B1420" stroke="rgba(217,116,35,0.75)" strokeWidth="1" />
          <text x="320" y="164" textAnchor="middle" fontFamily="ui-sans-serif, system-ui, sans-serif" fontSize="10" fontWeight="800" letterSpacing="1" fill="#F4F1EC">
            PROJECT LOCATION
          </text>
        </g>

        {/* Compass rose (top-left), subtle, product-like */}
        <g transform="translate(38,42)">
          <circle r="15" fill="rgba(11,20,32,0.7)" stroke="rgba(244,241,236,0.35)" />
          <path d="M 0 -11 L 3 0 L 0 11 L -3 0 Z" fill="#F4F1EC" />
          <text y="-19" textAnchor="middle" fontSize="9" fontWeight="800" fill="#F4F1EC" fontFamily="ui-sans-serif, system-ui, sans-serif" letterSpacing="1">N</text>
        </g>

        {/* Scale bar (bottom-left) */}
        <g transform="translate(40,340)" fontFamily="ui-sans-serif, system-ui, sans-serif">
          <line x1="0" y1="0" x2="120" y2="0" stroke="#F4F1EC" strokeWidth="1.5" opacity="0.85" />
          <line x1="0" y1="-4" x2="0" y2="4" stroke="#F4F1EC" strokeWidth="1.5" opacity="0.85" />
          <line x1="60" y1="-3" x2="60" y2="3" stroke="#F4F1EC" strokeWidth="1.5" opacity="0.65" />
          <line x1="120" y1="-4" x2="120" y2="4" stroke="#F4F1EC" strokeWidth="1.5" opacity="0.85" />
          <text x="0" y="18" textAnchor="start" fontSize="9" fontWeight="700" letterSpacing="0.5" fill="#94B4DC">0 mi</text>
          <text x="120" y="18" textAnchor="end" fontSize="9" fontWeight="700" letterSpacing="0.5" fill="#94B4DC">200 mi</text>
        </g>

        {/* Coordinate ticks (bottom-right), reinforce the map feel */}
        <g transform="translate(560,340)" fontFamily="ui-sans-serif, system-ui, sans-serif" fill="#94B4DC" opacity="0.85">
          <text x="0" y="0" fontSize="9" fontWeight="700" textAnchor="end" letterSpacing="0.5">ILLUSTRATIVE COVERAGE</text>
        </g>
      </svg>

      {/* Legend chips */}
      <div className="tmv__legend" data-testid="talent-matching-legend">
        <div className="tmv__leg">
          <span className="tmv__leg__dot" style={{ background: "#E85670" }} />
          <div style={{ minWidth: 0 }}>
            <div className="tmv__leg__txt">Local First</div>
            <div className="tmv__leg__sub">~100 mi from project</div>
          </div>
        </div>
        <div className="tmv__leg">
          <span className="tmv__leg__dot" style={{ background: "#F4A261" }} />
          <div style={{ minWidth: 0 }}>
            <div className="tmv__leg__txt">Regional</div>
            <div className="tmv__leg__sub">Adjacent counties and states</div>
          </div>
        </div>
        <div className="tmv__leg">
          <span className="tmv__leg__dot" style={{ background: "#94B4DC" }} />
          <div style={{ minWidth: 0 }}>
            <div className="tmv__leg__txt">Travel Ready</div>
            <div className="tmv__leg__sub">Nationwide when needed</div>
          </div>
        </div>
      </div>

      <figcaption className="tmv__caption" data-testid="talent-matching-note">
        <p>
          <MapPin className="inline h-3.5 w-3.5 mr-1.5" style={{ color: "#D97423", verticalAlign: "-2px" }} aria-hidden="true" />
          <strong>How Onward matches talent:</strong> we begin near the project, expand regionally,
          and then consider experienced travel ready professionals.
        </p>
      </figcaption>
    </figure>
  );
};

export default TalentMatchingVisual;
